// Design agent: MasqIP's stated goal is to teach subnetting by hand, not just calculate it. This
// popover is the "show your work" panel: it takes any address already shown in the inspector and
// renders exactly how a learner would decompose it on paper, one octet per row, bits above their
// power of two and the resulting sum below.
// Developer agent: Pure presentational component — all arithmetic lives in utils/binaryBreakdown.js
// and all placement/visibility logic lives in HoverableAddress.js, so this file only maps data onto
// markup and wires the two clipboard actions.
import {
  decomposeOctet,
  buildOctetEquationTerms,
  getOctetBitRoles,
  buildDottedBinary,
} from '../utils/binaryBreakdown';
import './BinaryBreakdown.css';

// Design agent: Splits a decomposed octet into its two nibbles (4 bits each) so the rendered bit
// string reproduces the "1100 0000" grouping asked for in spec 003 instead of one solid run of
// eight digits that is harder to read at a glance.
// Developer agent: Returns two arrays of length 4; callers zip them back with getOctetBitRoles by
// index (0-3 for the first nibble, 4-7 for the second).
const splitIntoNibbles = (bits) => [bits.slice(0, 4), bits.slice(4, 8)];

// Design agent: Every power of two owns one stable colour (see the --md-sys-color-power-* tokens in
// styles/materialTheme.css) so the "2^7" label above a bit and the "128" term in the sum below are
// visibly the same thing. The colour depends only on the bit position, never on the address, so a
// learner builds the same colour/weight association across every popover they open.
// Developer agent: Returns a CSS var() reference rather than a literal colour, which keeps a future
// palette revision confined to materialTheme.css.
const getPowerColor = (bitPosition) => `var(--md-sys-color-power-${bitPosition})`;

// Design agent: Copies text to the clipboard when the browser exposes the Clipboard API, and stays
// silent otherwise (older browsers, denied permission, insecure context, or a call that the browser
// does not consider a user gesture) rather than breaking the popover over a nice-to-have action.
// Developer agent: Centralised so both copy buttons share one error-handling path. The whole body is
// wrapped, not only the await, because reading navigator.clipboard can itself throw in a sandboxed
// document, and the rejected promise is swallowed so no "unhandled rejection" ever reaches the page.
const copyToClipboard = (text) => {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      const copyResult = navigator.clipboard.writeText(text);
      if (copyResult && typeof copyResult.catch === 'function') {
        copyResult.catch(() => {
          // Design agent: Permission denied or an unfocused document — nothing to tell the user
          // about, the popover keeps working and the value is still readable on screen.
        });
      }
    }
  } catch (error) {
    // Design agent: Same rationale as above, for browsers that throw synchronously.
  }
};

// Design agent: Renders the full binary breakdown of one IPv4 address: four octet rows, each with
// its bits grouped by nibble, the power of two above every set bit, and the decimal sum equation
// below. Bit digits keep the inspector's network/host colouring (so the popover still shows where
// the mask boundary falls), while the power labels and the decimal terms of the sum use the
// per-power palette so each "2^n" can be matched with its own decimal weight at a glance.
// Developer agent: `prefix` may be 32 (host-only) or any 0-32 value; getOctetBitRoles handles the
// full range without special-casing here. Placement, visibility and pointer handling are injected by
// the parent (HoverableAddress) through containerRef/style/on* props so this component stays purely
// presentational and can be rendered through a portal.
function BinaryBreakdown({
  id,
  address,
  prefix,
  networkColor,
  hostColor,
  label,
  containerRef = null,
  style = undefined,
  onMouseEnter = undefined,
  onMouseLeave = undefined,
  onKeyDown = undefined,
  onBlur = undefined,
}) {
  const octets = address.split('.').map(Number);

  // Developer agent: Right and middle clicks never reach a React onClick in Chrome, but other
  // engines are less strict, so both copy handlers explicitly ignore any non-primary button. A
  // keyboard activation (Enter/Space on the button) also reports button 0, so it stays allowed.
  const isPrimaryActivation = (event) => !event || event.button === undefined || event.button === 0;

  const handleCopyBinary = (event) => {
    if (!isPrimaryActivation(event)) {
      return;
    }
    copyToClipboard(buildDottedBinary(address));
  };

  const handleCopyDecimal = (event) => {
    if (!isPrimaryActivation(event)) {
      return;
    }
    copyToClipboard(address);
  };

  return (
    <div
      className="binary-breakdown-popover"
      id={id}
      ref={containerRef}
      role="dialog"
      aria-label={`Binary breakdown of ${address}`}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
    >
      <div className="binary-breakdown-header" id={`${id}-header`}>
        <div className="binary-breakdown-heading" id={`${id}-heading`}>
          {label && (
            <span className="binary-breakdown-label" id={`${id}-label`}>
              {label}
            </span>
          )}
          <span className="binary-breakdown-address" id={`${id}-address`}>
            {address}
          </span>
        </div>
        {/* Design agent: One short line is enough to turn the colour coding from decoration into a
            reading instruction — without it a first-time reader has to guess why "2^6" and "64" are
            the same shade of blue. */}
        <p className="binary-breakdown-legend" id={`${id}-legend`}>
          One colour per power of two: each 2<sup>n</sup> above a bit matches its value in the sum.
        </p>
      </div>

      <div className="binary-breakdown-rows" id={`${id}-rows`}>
        {octets.map((octetValue, octetIndex) => {
          const bits = decomposeOctet(octetValue);
          const roles = getOctetBitRoles(octetIndex, prefix);
          const [firstNibbleBits, secondNibbleBits] = splitIntoNibbles(bits);
          const equation = buildOctetEquationTerms(octetValue);
          const rowId = `${id}-octet-${octetIndex}`;

          // Design agent: Renders one nibble (4 bits) as a matched pair of power/bit columns so the
          // "2^7 / 1" and "2^6 / 1" stacks stay vertically aligned above their bit.
          const renderNibble = (nibbleBits, nibbleOffset, nibbleLabel) => (
            <span className="binary-breakdown-nibble" id={`${rowId}-nibble-${nibbleLabel}`}>
              {nibbleBits.map((entry, indexInNibble) => {
                const indexInOctet = nibbleOffset + indexInNibble;
                const role = roles[indexInOctet];
                const bitColor = role === 'network' ? networkColor : hostColor;
                return (
                  <span
                    className="binary-breakdown-bit-column"
                    id={`${rowId}-bit-${entry.bit}`}
                    key={entry.bit}
                  >
                    <span
                      className="binary-breakdown-power"
                      id={`${rowId}-bit-${entry.bit}-power`}
                      style={{
                        color: getPowerColor(entry.bit),
                        visibility: entry.isSet ? 'visible' : 'hidden',
                      }}
                    >
                      2<sup>{entry.bit}</sup>
                    </span>
                    <span
                      className="binary-breakdown-bit"
                      id={`${rowId}-bit-${entry.bit}-value`}
                      style={{ color: bitColor }}
                    >
                      {entry.isSet ? '1' : '0'}
                    </span>
                  </span>
                );
              })}
            </span>
          );

          return (
            <div className="binary-breakdown-row" id={rowId} key={rowId}>
              <div className="binary-breakdown-bits-line" id={`${rowId}-bits-line`}>
                {renderNibble(firstNibbleBits, 0, 'high')}
                {renderNibble(secondNibbleBits, 4, 'low')}
              </div>
              {/* Design agent: The sum repeats the power colours term by term, so "128 + 64 = 192"
                  reads as the same two colours the reader just saw above the two set bits. The "+"
                  signs and the total stay neutral so the coloured terms carry the meaning. */}
              <div className="binary-breakdown-equation" id={`${rowId}-equation`}>
                {equation.terms.length === 0 ? (
                  <span className="binary-breakdown-term" id={`${rowId}-term-zero`}>
                    0
                  </span>
                ) : (
                  equation.terms.map((term, termIndex) => (
                    <span key={term.bit} id={`${rowId}-term-group-${term.bit}`}>
                      {termIndex > 0 && (
                        <span className="binary-breakdown-operator" id={`${rowId}-plus-${term.bit}`}>
                          {' + '}
                        </span>
                      )}
                      <span
                        className="binary-breakdown-term"
                        id={`${rowId}-term-${term.bit}`}
                        style={{
                          color: getPowerColor(term.bit),
                          backgroundColor: `rgba(var(--md-sys-color-power-${term.bit}-rgb), 0.12)`,
                        }}
                      >
                        {term.power}
                      </span>
                    </span>
                  ))
                )}
                <span className="binary-breakdown-operator" id={`${rowId}-equals`}>
                  {' = '}
                </span>
                <span className="binary-breakdown-total" id={`${rowId}-total`}>
                  {equation.total}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="binary-breakdown-actions" id={`${id}-actions`}>
        <button
          type="button"
          className="binary-breakdown-copy-button"
          id={`${id}-copy-binary`}
          onClick={handleCopyBinary}
        >
          Copy binary
        </button>
        <button
          type="button"
          className="binary-breakdown-copy-button"
          id={`${id}-copy-decimal`}
          onClick={handleCopyDecimal}
        >
          Copy dotted decimal
        </button>
      </div>
    </div>
  );
}

export default BinaryBreakdown;
